import React, { memo, useCallback, useMemo, useState } from 'react'
import { ISelectOption } from 'components/forms/Selects/SelectOption'
import pick from 'lodash/pick'
import Card from 'transfix-ui/components/Card'
import { SingleSelect } from 'transfix-ui/components/SingleSelect'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { Maybe } from 'types/graphqlTypes'
import MarketInsightsLocationData from './MarketInsightsLocationData'
import MarketInsightsLocationDataSkeleton from './MarketInsightsLocationData/MarketInsightsLocationDataSkeleton'
import { DAY_RANGE_OPTIONS, MARKET_INSIGHT_RANGE_KEYS, TEST_IDS } from './constants'
import {
  useLaneMarketInsightsQuery,
  ILaneMarketInsightsQueryVariables,
} from './graphql/LaneMarketInsights'
import { IDayRange, TMarketInsightsLocation } from './types'

export interface IMarketInsightsCardProps {
  loadingLaneData?: boolean
  deliveryLocation: Maybe<TMarketInsightsLocation>
  pickupLocation: Maybe<TMarketInsightsLocation>
}
export const MarketInsightsCard = memo<IMarketInsightsCardProps>(
  ({ deliveryLocation, loadingLaneData = false, pickupLocation }) => {
    const [dayRange, setDayRange] = useState<IDayRange>(DAY_RANGE_OPTIONS[0].value)
    const marketInsightRangeKey = MARKET_INSIGHT_RANGE_KEYS[dayRange]

    const selectHandler = useCallback(
      (selected?: ISelectOption<IDayRange>) => setDayRange(curr => selected?.value ?? curr),
      []
    )

    const queryVariables: ILaneMarketInsightsQueryVariables | undefined = useMemo(() => {
      if (deliveryLocation && pickupLocation) {
        return {
          pickupLocation: pick(pickupLocation, 'city', 'stateCode'),
          deliveryLocation: pick(deliveryLocation, 'city', 'stateCode'),
        }
      }

      return undefined
    }, [deliveryLocation, pickupLocation])

    const { data, loading: loadingLaneMarketInsights } = useLaneMarketInsightsQuery({
      variables: queryVariables,
      skip: !queryVariables,
    })

    const { laneMarketInsights } = data ?? {}
    const { deliveryMarketInsight, pickupMarketInsight } = laneMarketInsights ?? {}

    const { datCityState: pickupDatCityState } = pickupMarketInsight ?? {}
    const { datCityState: deliveryDatCityState } = deliveryMarketInsight ?? {}

    const pickupMarketInsightValue = (pickupMarketInsight ?? {})[marketInsightRangeKey]
    const deliveryMarketInsightValue = (deliveryMarketInsight ?? {})[marketInsightRangeKey]

    const loading = loadingLaneMarketInsights || loadingLaneData

    return (
      <Card>
        <Grid container spacing={2}>
          <Grid container item xs={12} spacing={3}>
            <Grid item>
              <Typography variant='body1'>Market Insights</Typography>
            </Grid>

            <Grid item>
              <SingleSelect
                id='market-insights-day-range-select'
                dataTest={TEST_IDS.dayRangeSelect}
                clearable={false}
                label='Day Range'
                options={DAY_RANGE_OPTIONS}
                onSelect={selectHandler}
                selected={dayRange}
              />
            </Grid>
          </Grid>

          <Grid container item justifyContent='center' xs={6}>
            {loading ? (
              <MarketInsightsLocationDataSkeleton />
            ) : (
              <MarketInsightsLocationData
                dataTestId={TEST_IDS.pickupMarketInsight}
                datCityState={pickupDatCityState}
                location={pickupLocation}
                stopType='pickup'
                value={pickupMarketInsightValue}
              />
            )}
          </Grid>

          <Grid container item justifyContent='center' xs={6}>
            {loading ? (
              <MarketInsightsLocationDataSkeleton />
            ) : (
              <MarketInsightsLocationData
                dataTestId={TEST_IDS.deliveryMarketInsight}
                datCityState={deliveryDatCityState}
                location={deliveryLocation}
                stopType='delivery'
                value={deliveryMarketInsightValue}
              />
            )}
          </Grid>
        </Grid>
      </Card>
    )
  }
)

export default MarketInsightsCard
